import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const GeoTeaserSection: React.FC = () => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const handleNavigation = (path: string) => {
        window.location.href = path;
    };

    return (
        <section id="geo-teaser" className="py-24 bg-slate-950 text-white relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2"></div>
            </div>

            <div className="container mx-auto px-5 relative z-10">
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    {/* Left Side: Content */}
                    <motion.div
                        ref={ref}
                        initial={{ opacity: 0, x: -50 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8 }}
                        className="lg:w-1/2"
                    >
                        <div className="inline-block px-4 py-1 bg-indigo-500/20 text-indigo-400 rounded-full text-sm font-bold mb-6 border border-indigo-500/30">
                            The Next Frontier of Search
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tight leading-tight">
                            SEO is the <span className="text-slate-500">Foundation</span>.<br />
                            <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">GEO is the Future.</span>
                        </h2>
                        <p className="text-xl text-slate-400 mb-8 leading-relaxed">
                            70% of search journeys now involve AI engines like Gemini, Perplexity, and ChatGPT. If your brand isn't part of the AI-generated answer, you're invisible to the most motivated buyers.
                        </p>

                        <div className="space-y-4 mb-10">
                            {[
                                "Optimize for AI-Generated Overviews",
                                "Boost Citation Frequency in LLMs",
                                "Establish Semantic Brand Authority",
                                "Master Knowledge Graph Integration"
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-center gap-3">
                                    <div className="w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center shrink-0">
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" /></svg>
                                    </div>
                                    <span className="text-slate-300 font-medium">{item}</span>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => handleNavigation('/geo-expert')}
                            className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-bold text-lg transition-all shadow-[0_0_20px_rgba(99,102,241,0.4)] hover:scale-105"
                        >
                            Claim Your GEO Readiness Audit →
                        </button>
                    </motion.div>

                    {/* Right Side: Visual Element */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={inView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="lg:w-1/2 relative"
                    >
                        <div className="relative z-10 bg-white/5 backdrop-blur-xl rounded-[3rem] border border-white/10 p-2 overflow-hidden shadow-2xl">
                            <div className="bg-slate-900 rounded-[2.5rem] p-8">
                                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-white/5">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-2xl">🤖</div>
                                    <div>
                                        <div className="text-sm text-slate-500 font-bold uppercase tracking-widest mb-1">AI Search Analysis</div>
                                        <div className="text-xl font-bold bg-gradient-to-r from-blue-400 to-white bg-clip-text text-transparent italic">"Recommendation: Your Brand"</div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="h-4 bg-white/5 rounded-full w-3/4"></div>
                                    <div className="h-4 bg-white/5 rounded-full w-full"></div>
                                    <div className="h-4 bg-white/5 rounded-full w-5/6"></div>

                                    <div className="mt-8 p-4 bg-indigo-500/10 rounded-2xl border border-indigo-500/20">
                                        <div className="text-indigo-400 font-bold mb-2 flex items-center gap-2">
                                            <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></span>
                                            Semantic Citation Found
                                        </div>
                                        <div className="text-slate-400 text-sm">
                                            Entity identified as "Market Leader" in high-authority datasets.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Decorative circles */}
                        <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl animate-pulse"></div>
                        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default GeoTeaserSection;
